/**
 * The staged batch screen -- what an import would do, and the decisions that
 * let it (f8.2 preview, f8.3 decisions and commit).
 *
 * Reached from the Data import screen by **UUID**: `batch_number` is the label
 * rendered as `#0147` and is never routable, because every externally
 * reachable resource in Atlas is addressed by a non-enumerable id.
 *
 * **Everything on this page is read from the stored plan.** The *File parsed*
 * panel reads the batch row and recomputes none of it; the tiles and the
 * change table read `import_staged_node`; the decision sections read
 * `import_staged_role`. That is what makes the preview evidence rather than a
 * second opinion -- and it is what lets the commit write exactly what the
 * operator saw rather than whatever is true by the time they click.
 *
 * **No count on this screen is computed in the browser.** `outstanding`
 * arrives on the role-slots read, counted by the server from the same snapshot
 * the rows came from, and `batch_state` arrives beside it. The `Commit import`
 * button mirrors that state; it is a courtesy and never the boundary, and the
 * server refuses a commit from any state but `STAGED` regardless of what is
 * rendered here.
 *
 * **Three decisions, three controls, and they are separate on purpose.** An
 * unmatched name, a record the workbook names two holders for, and a code that
 * collided are different questions. Merging their counts would make the
 * operator hunt for a control the number does not point at.
 *
 * | Prototype | What is built instead | Why |
 * | --- | --- | --- |
 * | *"7 role holders matched no Atlas user"*, 147 staged rows, 1 dropped | whatever the batch actually holds — 43 names, 70 staged rows and 78 dropped on the real file | the prototype's numbers are illustrative |
 * | one row per unresolved CELL | one row per distinct HUMAN | `Axel Apice` is in fifteen cells across five roles and is one question; one human is one account |
 * | the commit bar in section 3's foot | in section 4's foot | the contested slots are a fourth section, and a commit control sitting above a decision it waits on reads as broken |
 * | *"the person record is created"* on the invite modal | *the account is created* | ADR-19's separate roster is superseded: the account IS the holder |
 * | an `Export list` button | not built | a resolutions CSV is out of scope until somebody asks for one |
 * | a `Scope` select on the invite modal | not built | scope is derived from ownership in f9.1 and is never a field on a user |
 * | `SAP 21902` in a Detail cell | the Kimble `KE` code | no sheet in this workbook has a SAP ID column |
 * | `Replace` on the parsed file | not built | an ingestion attempt is evidence; uploading again creates a new batch |
 * | a `Cycle` row on the batch panel | not built | the master workbook is not cycle-scoped |
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  CheckCircle2,
  FileSpreadsheet,
  Info,
  ShieldAlert,
  UserPlus,
  Users,
  X,
} from 'lucide-react';

import {
  ApiError,
  type LookupValue,
  type RoleHolderDecision,
  type StagedNode,
} from '@/api/client';
import {
  useCommitBatch,
  useDiscardBatch,
  useJobProgress,
  useLookupCatalog,
  useRoleSlots,
  useSetStagedNodeCode,
  useStagedPlan,
} from '@/api/queries';
import { JobProgress } from '@/jobs/JobProgress';
import { DELIVERY_ROLE_TYPE } from '@/routes/business-unit-groups/ownershipView';
import { EmptyCell } from '@/ui/EmptyCell';
import { platformRolesFrom } from '@/users/platformRoles';
import type { InvitedAccount } from '@/users/InviteUserDialog';

import { AssignExistingDialog } from './AssignExistingDialog';
import { ContestedSlots } from './ContestedSlots';
import { InviteRoleHolderDialog } from './InviteRoleHolderDialog';
import {
  actionPresentation,
  batchLabel,
  detailFor,
  droppedExplanation,
  formatBytes,
  formatWhen,
  isOpen,
  levelLabel,
  levelTiles,
  needsCode,
  orderedChanges,
  shortChecksum,
  slotPresentation,
  statePresentation,
} from './importView';
import {
  appearsOnSummary,
  commitBlockedReason,
  isCommitRunning,
  isDecidable,
  otherDecisions,
  resolutionProgress,
  roleLabel,
  unresolvedNamesTitle,
} from './resolutionView';

function BackLink() {
  return (
    <Link className="btn btn-secondary" to="/data-import">
      <ArrowLeft aria-hidden="true" /> Data import
    </Link>
  );
}

/**
 * A staged node's row in the change table, with the code editor when its
 * derived code collided.
 *
 * `proposed_values` is never rewritten -- the human's code goes to
 * `resolved_code`, so what the derivation proposed and what the operator chose
 * stay distinguishable. The field is validated by the same `validated_code()`
 * the Business Unit Group CRUD route uses, server-side, and a collision is
 * refused with the same code that route returns.
 */
function ChangeRow({
  node,
  batchId,
  editable,
}: {
  node: StagedNode;
  batchId: string;
  editable: boolean;
}) {
  const action = actionPresentation(node.action);
  const setCode = useSetStagedNodeCode(batchId);
  const [code, setCode$] = useState(node.resolved_code ?? '');
  const outstanding = needsCode(node) && !node.resolved_code;

  return (
    <tr>
      <td>
        <span className={`badge ${action.pill}`}>{action.label}</span>
      </td>
      <td>{levelLabel(node.level)}</td>
      <td className="truncate">{node.natural_key}</td>
      <td className="truncate">{node.parent_natural_key ?? <EmptyCell />}</td>
      <td className="hint">
        {detailFor(node)}
        {node.resolved_code ? (
          <span className="badge pill-success ml-2">Code {node.resolved_code}</span>
        ) : null}
        {outstanding ? (
          editable ? (
            <div className="code-fix mt-4">
              <label className="sr-only" htmlFor={`code-${node.id}`}>
                Code for {node.natural_key}
              </label>
              <input
                id={`code-${node.id}`}
                type="text"
                value={code}
                placeholder="ASSET_MANAGEMENT"
                maxLength={16}
                disabled={setCode.isPending}
                onChange={(event) => setCode$(event.target.value.toUpperCase())}
              />
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={setCode.isPending || code.trim().length === 0}
                onClick={() => setCode.mutate({ stagedNodeId: node.id, code })}
              >
                <Check aria-hidden="true" /> Save code
              </button>
            </div>
          ) : (
            <span className="badge pill-warning ml-2">Needs a code</span>
          )
        ) : null}
        {setCode.error instanceof ApiError ? (
          <span className="field-error" role="alert">
            {setCode.error.message}
          </span>
        ) : null}
      </td>
    </tr>
  );
}

/**
 * One distinct human, with the evidence the prototype draws.
 *
 * The primary emphasis flips to `Create new` when nothing was suggested, and
 * `Create new` is **absent** -- not disabled -- for an `AMBIGUOUS` name:
 * offering to invite a third account for a name that already has two is how
 * the duplicate got there.
 */
function NameRow({
  decision,
  deliveryRoles,
  disabled,
  onAssign,
  onInvite,
}: {
  decision: RoleHolderDecision;
  deliveryRoles: readonly LookupValue[];
  disabled: boolean;
  onAssign: () => void;
  onInvite: () => void;
}) {
  const presentation = slotPresentation(decision.slot_state);
  // `candidates` is populated only for an AMBIGUOUS name -- the accounts it
  // already matched -- and is optional on the wire for every other one.
  const matched = decision.candidates ?? [];
  const suggested = matched[0] ?? decision.bound_user ?? null;
  // The prototype's flip: a confident suggestion makes `Assign existing` the
  // primary action; nothing to suggest makes `Create new` the likely answer.
  const assignIsPrimary = matched.length > 0;

  return (
    <tr>
      <td>
        <b>{decision.raw_names.join(' / ')}</b>
        <span className="sub">normalised: {decision.normalized_key}</span>
      </td>
      <td>
        <span className="btn-row">
          {decision.roles.map((occupancy) => (
            <span key={occupancy.delivery_role_code} className="badge badge-chip">
              {roleLabel(occupancy.delivery_role_code, deliveryRoles)}
              {occupancy.slot_count > 1 ? ` ×${occupancy.slot_count}` : ''}
            </span>
          ))}
        </span>
      </td>
      <td className="hint truncate">{appearsOnSummary(decision)}</td>
      <td>
        {suggested ? (
          <span className="row">
            <span>
              {suggested.display_name}
              <span className="sub">{suggested.email}</span>
            </span>
          </span>
        ) : (
          <span className="hint">No confident match</span>
        )}
      </td>
      <td>
        {decision.is_resolved ? (
          <span className="badge pill-success">
            <Check aria-hidden="true" /> Resolved
          </span>
        ) : (
          <span className="btn-row">
            <button
              type="button"
              className={`btn btn-sm ${assignIsPrimary ? 'btn-primary' : 'btn-secondary'}`}
              disabled={disabled}
              onClick={onAssign}
            >
              <Users aria-hidden="true" /> Assign existing
            </button>
            {decision.offers_invite ? (
              <button
                type="button"
                className={`btn btn-sm ${assignIsPrimary ? 'btn-secondary' : 'btn-primary'}`}
                disabled={disabled}
                onClick={onInvite}
              >
                <UserPlus aria-hidden="true" /> Create new
              </button>
            ) : null}
            <span className={`badge ${presentation.pill}`}>{presentation.label}</span>
          </span>
        )}
      </td>
    </tr>
  );
}

export function StagedBatchPage() {
  const { batchId } = useParams<{ batchId: string }>();
  const plan = useStagedPlan(batchId);
  const slots = useRoleSlots(batchId);
  const catalog = useLookupCatalog();
  const discard = useDiscardBatch();
  const commit = useCommitBatch(batchId);

  const [assigning, setAssigning] = useState<RoleHolderDecision | null>(null);
  const [inviting, setInviting] = useState<RoleHolderDecision | null>(null);
  const [invited, setInvited] = useState<InvitedAccount | null>(null);

  const commitJobId = plan.data?.batch.commit_job_id ?? undefined;
  const commitJob = useJobProgress(commitJobId);

  if (plan.error instanceof ApiError) {
    const forbidden = plan.error.status === 403;
    return (
      <>
        <div className="page-head">
          <div>
            <h1 className="page-title">Staged batch</h1>
          </div>
          <div className="page-actions">
            <BackLink />
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <div className="empty">
              <div className="e-icon e-icon-warning">
                <ShieldAlert aria-hidden="true" />
              </div>
              <h3>{forbidden ? 'No access' : 'Not found'}</h3>
              <p>
                {forbidden
                  ? 'Data import is administrators only, and the refusal is recorded in the audit log.'
                  : 'There is no such ingestion batch, or it is not one you may see.'}
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (plan.isLoading || !plan.data) {
    return (
      <div className="card" aria-busy="true" aria-label="Loading the staged batch">
        <div className="card-body">
          {[0, 1, 2, 3].map((index) => (
            <span key={index} className="skeleton-bar group-skeleton-line" />
          ))}
        </div>
      </div>
    );
  }

  const { batch, levels, nodes, unchanged_total, issues } = plan.data;
  const state = statePresentation(batch.state);
  const tiles = levelTiles(levels);
  const changes = orderedChanges(nodes);
  const issueCodes = issues.map((issue) => issue.code);
  const abandonable = isOpen(batch.state);

  const deliveryRoles =
    catalog.data?.types.find((type) => type.code === DELIVERY_ROLE_TYPE)?.values ?? [];
  const platformRoles = platformRolesFrom(catalog.data);

  // Everything below is the SERVER's arithmetic, read off one payload.
  const decisions = slots.data?.role_holders ?? [];
  const unresolved = decisions.filter((decision) => !decision.is_resolved);
  const contested = slots.data?.contested_slots ?? [];
  const outstanding = slots.data?.outstanding ?? null;
  const batchState = slots.data?.batch_state ?? batch.state;
  const decidable = isDecidable(batchState);

  const running = isCommitRunning(commitJob.data);
  const blocked = commitBlockedReason(batchState, outstanding, commitJob.data);

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">
            Staged batch {batchLabel(batch.batch_number)}
          </h1>
          <div className="page-sub">
            Master workbook &middot; uploaded {formatWhen(batch.uploaded_at)} &middot;
            {batch.committed_at
              ? ` committed ${formatWhen(batch.committed_at)}`
              : ' nothing is written until this batch is committed'}
          </div>
        </div>
        <div className="page-actions">
          <BackLink />
          {abandonable ? (
            <button
              type="button"
              className="btn btn-danger"
              disabled={discard.isPending || running}
              onClick={() => discard.mutate(batch.id)}
            >
              <X aria-hidden="true" /> Discard batch
            </button>
          ) : null}
        </div>
      </div>

      {discard.error instanceof ApiError ? (
        <div className="alert alert-danger mb-4" role="alert">
          <AlertTriangle aria-hidden="true" />
          <div>
            <div className="a-title">The batch was not discarded</div>
            <div className="a-body">{discard.error.message}</div>
          </div>
        </div>
      ) : null}

      {invited ? (
        <div className="alert alert-info mb-4">
          <CheckCircle2 aria-hidden="true" />
          <div>
            <div className="a-title">{invited.display_name} now has an Atlas account</div>
            <div className="a-body">
              {invited.email} was created with no permissions and every slot this name
              holds is bound to it. Nothing has been written to the hierarchy — that
              happens when the batch is committed.
            </div>
          </div>
        </div>
      ) : null}

      {batch.state === 'REJECTED' ? (
        <div className="alert alert-danger mb-4">
          <AlertTriangle aria-hidden="true" />
          <div>
            <div className="a-title">This batch was refused</div>
            <div className="a-body">
              {batch.rejection_reason ?? 'The file could not be read.'} &mdash; nothing
              was staged and nothing was written. Correct the file and upload it again;
              the original is kept so the attempt stays inspectable.
            </div>
          </div>
        </div>
      ) : batchState === 'COMMITTED' ? (
        <div className="alert alert-info mb-4">
          <CheckCircle2 aria-hidden="true" />
          <div>
            <div className="a-title">This batch has been committed</div>
            <div className="a-body">
              The hierarchy and every delivery-role assignment it named were written in
              one transaction. The staged rows are kept, so what was decided stays
              readable.
            </div>
          </div>
        </div>
      ) : outstanding && outstanding.total > 0 ? (
        <div className="alert alert-warning mb-4">
          <AlertTriangle aria-hidden="true" />
          <div>
            <div className="a-title">{unresolvedNamesTitle(outstanding.names)}</div>
            <div className="a-body">
              The import is staged and will not commit until every one is bound to an
              existing user or given an account. <strong>Nothing has been written
              yet.</strong>
              {otherDecisions(outstanding).length > 0 ? (
                <>
                  {' '}
                  There {otherDecisions(outstanding).length === 1 ? 'is also' : 'are also'}{' '}
                  {otherDecisions(outstanding).join(' and ')} to settle — a different
                  question, with its own control below.
                </>
              ) : null}
            </div>
          </div>
        </div>
      ) : (
        <div className="alert alert-info mb-4">
          <CheckCircle2 aria-hidden="true" />
          <div>
            <div className="a-title">Every decision has been made</div>
            <div className="a-body">
              Nothing has been written. Committing applies the whole plan in one
              transaction, or none of it.
            </div>
          </div>
        </div>
      )}

      <div className="split-2-1">
        <div className="stack">
          <div className="card">
            <div className="card-head">
              <h3>1 &middot; File parsed</h3>
              <span className={`badge ${state.pill}`}>{state.label}</span>
            </div>
            <div className="card-body">
              <div className="dropzone is-filled">
                <FileSpreadsheet className="dz-icon" aria-hidden="true" />
                <div className="dz-title truncate">
                  {batch.original_filename ?? 'The uploaded file'}
                </div>
                <div className="hint">
                  {formatBytes(batch.byte_size)} &middot; uploaded{' '}
                  {formatWhen(batch.uploaded_at)}
                </div>
              </div>

              <dl className="dl cols-4 mt-6">
                <div>
                  <dt>Detected format</dt>
                  <dd>
                    {batch.detected_format}{' '}
                    <span className="hint">(by content, not extension)</span>
                  </dd>
                </div>
                <div>
                  <dt>Header row</dt>
                  <dd>
                    {batch.detected_header_row === null ? (
                      <EmptyCell />
                    ) : (
                      <>
                        Row {batch.detected_header_row}{' '}
                        <span className="hint">detected</span>
                      </>
                    )}
                  </dd>
                </div>
                <div>
                  <dt>Sheets read</dt>
                  <dd>{plan.data.sheets_read.join(', ')}</dd>
                </div>
                <div>
                  <dt>Checksum</dt>
                  <dd className="mono">{shortChecksum(batch.content_sha256)}</dd>
                </div>
                <div>
                  <dt>Rows in</dt>
                  <dd>{batch.rows_in ?? <EmptyCell />}</dd>
                </div>
                <div>
                  <dt>Rows staged</dt>
                  <dd>{batch.rows_out ?? <EmptyCell />}</dd>
                </div>
                <div>
                  <dt>Rows dropped</dt>
                  <dd>
                    {batch.rows_dropped ?? 0}{' '}
                    <span className="hint">
                      {droppedExplanation(batch.rows_dropped, issueCodes)}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt>Source system</dt>
                  <dd>{batch.source_system}</dd>
                </div>
              </dl>
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <h3>2 &middot; What will be created</h3>
              <span className="hint">
                {batchState === 'COMMITTED'
                  ? 'Written'
                  : 'Nothing is written until this is committed'}
              </span>
            </div>
            <div className="card-body">
              <div className="stat-grid mb-4">
                {tiles.map((tile) => (
                  <div key={tile.level} className="stat">
                    <div className="stat-label">{tile.label}s</div>
                    <div className="stat-value">{tile.total}</div>
                    <div className="stat-foot">
                      {tile.created ? (
                        <span className="delta-up">+{tile.created} new</span>
                      ) : null}
                      {tile.created && tile.foot ? ' · ' : ''}
                      {tile.foot}
                    </div>
                  </div>
                ))}
              </div>

              <div className="table-scroll">
                <table className="data compact">
                  <thead>
                    <tr>
                      <th>Action</th>
                      <th>Level</th>
                      <th>Record</th>
                      <th>Parent</th>
                      <th>Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {changes.map((node) => (
                      <ChangeRow
                        key={node.id}
                        node={node}
                        batchId={batch.id}
                        editable={decidable}
                      />
                    ))}
                    {unchanged_total > 0 ? (
                      <tr>
                        <td>
                          <span className="badge pill-neutral">Unchanged</span>
                        </td>
                        <td>
                          <EmptyCell />
                        </td>
                        <td colSpan={3} className="hint">
                          {unchanged_total} further record
                          {unchanged_total === 1 ? '' : 's'} resolve to an existing row
                          with no change
                        </td>
                      </tr>
                    ) : null}
                    {changes.length === 0 && unchanged_total === 0 ? (
                      <tr>
                        <td colSpan={5} className="hint">
                          Nothing was staged for this batch.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <h3>3 &middot; Role holders that need a decision</h3>
              {outstanding && outstanding.names > 0 ? (
                <span className="badge pill-warning">{outstanding.names} unresolved</span>
              ) : (
                <span className="badge pill-success">All resolved</span>
              )}
            </div>
            <div className="card-body">
              <p className="hint mb-4">
                Each row is <strong>one person</strong>, not one cell: a name holding
                several delivery roles is one decision, and resolving it binds every
                slot that name occupies in this workbook. Bind it to an existing
                account, or give it one.
              </p>
              <div className="table-scroll">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Name in workbook</th>
                      <th>Roles held</th>
                      <th>Appears on</th>
                      <th>Suggested match</th>
                      <th style={{ width: '330px' }}>Resolution</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unresolved.map((decision) => (
                      <NameRow
                        key={decision.normalized_key}
                        decision={decision}
                        deliveryRoles={deliveryRoles}
                        disabled={!decidable}
                        onAssign={() => setAssigning(decision)}
                        onInvite={() => setInviting(decision)}
                      />
                    ))}
                    {unresolved.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="hint">
                          Every role holder in this workbook is bound to exactly one
                          account.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="card-foot">
              <span className="hint">
                {resolutionProgress(decisions, outstanding?.names ?? 0)}
              </span>
            </div>
          </div>

          <ContestedSlots
            batchId={batch.id}
            slots={contested}
            deliveryRoles={deliveryRoles}
            disabled={!decidable}
          />

          <div className="card">
            <div className="card-head">
              <h3>5 &middot; Commit</h3>
              <span className={`badge ${state.pill}`}>{state.label}</span>
            </div>
            <div className="card-body stack-sm">
              <p className="hint">
                Committing writes the hierarchy and every delivery-role assignment in
                <strong> one transaction</strong>: all of it, or none of it. Records
                missing from the workbook are never deleted &mdash; deactivate them
                from their own list.
              </p>
              {commit.error instanceof ApiError ? (
                <div className="alert alert-danger" role="alert">
                  <AlertTriangle aria-hidden="true" />
                  <div>
                    <div className="a-title">The commit was refused</div>
                    <div className="a-body">{commit.error.message}</div>
                  </div>
                </div>
              ) : null}
              {commitJob.data ? (
                <JobProgress job={commitJob.data} title="Committing the plan" />
              ) : null}
            </div>
            <div className="card-foot">
              <span className="hint">
                {blocked ?? 'Every decision has been made. This batch can be committed.'}
              </span>
              <div className="btn-row">
                <button
                  type="button"
                  className={`btn btn-primary${blocked ? ' is-disabled' : ''}`}
                  /* Disabled for real as well as in appearance -- and the
                     server refuses a commit from any state but STAGED either
                     way, so this is a courtesy and never the boundary. */
                  disabled={blocked !== null || commit.isPending}
                  onClick={() => commit.mutate()}
                >
                  <Check aria-hidden="true" /> Commit import
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="stack">
          <div className="card">
            <div className="card-head">
              <h3>Batch</h3>
            </div>
            <div className="card-body stack-sm">
              <div className="row">
                <span className="hint dl-key">Feed</span>
                <span>{batch.source_system}</span>
              </div>
              <div className="row">
                <span className="hint dl-key">Adapter</span>
                <span>{batch.adapter_type}</span>
              </div>
              <div className="row">
                <span className="hint dl-key">Status</span>
                <span className={`badge ${state.pill}`}>{state.label}</span>
              </div>
              <div className="row">
                <span className="hint dl-key">Uploaded at</span>
                <span>{formatWhen(batch.uploaded_at)}</span>
              </div>
              {batch.committed_at ? (
                <div className="row">
                  <span className="hint dl-key">Committed at</span>
                  <span>{formatWhen(batch.committed_at)}</span>
                </div>
              ) : null}
              <div className="row">
                <span className="hint dl-key">Findings</span>
                <span>
                  {batch.issues.error} error &middot; {batch.issues.warning} warning
                  &middot; {batch.issues.info} info
                </span>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <h3>Issues</h3>
            </div>
            <div className="card-body stack-sm">
              {issues.length === 0 ? (
                <div className="hint">Nothing was raised while reading this file.</div>
              ) : (
                issues.map((issue) => (
                  <div key={`${issue.severity}-${issue.code}`} className="row">
                    <span
                      className={`badge ${
                        issue.severity === 'ERROR'
                          ? 'pill-danger'
                          : issue.severity === 'WARNING'
                            ? 'pill-warning'
                            : 'pill-info'
                      }`}
                    >
                      {issue.count}
                    </span>
                    <span className="issue-code" title={issue.example_message}>
                      {issue.code.replace(/_/g, ' ')}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <h3>
                {batchState === 'COMMITTED' ? 'What was written' : 'Nothing is written yet'}
              </h3>
            </div>
            <div className="card-body">
              <div className="alert alert-neutral">
                <Info aria-hidden="true" />
                <div className="a-body">
                  {batchState === 'COMMITTED'
                    ? 'The hierarchy and its ownership. No record absent from the workbook was deleted, deactivated or ended — absence is not an instruction to remove anything.'
                    : 'This batch has created no Business Unit Group, Business Unit, Client, Engagement or ownership assignment. Discarding it keeps the plan so the decision stays inspectable.'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {assigning ? (
        <AssignExistingDialog
          batchId={batch.id}
          decision={assigning}
          deliveryRoles={deliveryRoles}
          onClose={() => setAssigning(null)}
        />
      ) : null}

      {inviting ? (
        <InviteRoleHolderDialog
          batchId={batch.id}
          decision={inviting}
          platformRoles={platformRoles}
          onInvited={setInvited}
          onClose={() => setInviting(null)}
        />
      ) : null}
    </>
  );
}
